import { LumiDB } from "@lumidb/lumidb";
import { subscribe } from "valtio";
import { subscribeKey } from "valtio/utils";
import { Annotations, isLineStringFeature, isPolygonFeature } from "./annotations";
import { App } from "./app";
import { Auth } from "./auth";
import { CrossSection } from "./cross-section";
import { EarthControls } from "./earth-controls";
import { History, Journal } from "./history";
import { HUD } from "./hud";
import { InputManager } from "./input-manager";
import { LOD } from "./lod";
import { Org } from "./org";
import { Prefs } from "./prefs";
import { Project } from "./project";
import { Scene } from "./scene";
import { Terrain } from "./terrain";
import { Thumbnail } from "./thumbnail";
import { Viewer } from "./viewer";

declare global {
    var __services__: undefined | ReturnType<typeof createServices>;
}

function getClipPolygon(app: App, annotations: Annotations): [number, number][] | null {
    if (annotations.state.input === "drafting") {
        return null;
    }

    const id = app.state.selectionId;
    const feature = id ? annotations.getFeature(id) : undefined;
    if (!feature || !isPolygonFeature(feature) || feature.properties.crop !== true) {
        return null;
    }

    const ring = feature.geometry.coordinates[0];
    if (ring.length < 3) return null;

    return ring.map((p) => {
        const { x, y } = annotations.wgs84ToLocal({ x: p[0], y: p[1], z: p[2] ?? 0 });
        return [x, y] as [number, number];
    });
}

function handleMaterialUpdate(
    app: App,
    viewer: ReturnType<typeof createViewer>,
    crossSection: ReturnType<typeof createCrossSection>,
) {
    const clip = getClipPolygon(app, viewer.annotations);
    viewer.lod.updateMaterials(clip);
    crossSection.lod.updateMaterials(clip);
    viewer.terrain.updateMaterials();
}

function handleCrossSectionConnect(
    app: App,
    viewer: ReturnType<typeof createViewer>,
    crossSection: ReturnType<typeof createCrossSection>,
) {
    const selectionId = app.state.selectionId;
    if (!selectionId) return;

    const feature = viewer.annotations.getFeature(selectionId);
    if (!feature) return;

    if (isLineStringFeature(feature) && feature.geometry.coordinates.length === 2) {
        const [sx, sy, sz] = feature.geometry.coordinates[0];
        const [ex, ey, ez] = feature.geometry.coordinates[1];
        const start = { x: sx, y: sy, z: sz ?? 0 };
        const end = { x: ex, y: ey, z: ez ?? 0 };

        if (crossSection.connect(selectionId, start, end)) {
            crossSection.annotations.dispose();
            crossSection.lod.eval().then(() => {
                crossSection.fit();
            });
        }
    }
}

function handleViewerSync(
    app: App,
    prefs: Prefs,
    viewer: ReturnType<typeof createViewer>,
    crossSection: ReturnType<typeof createCrossSection>,
) {
    subscribe(viewer.annotations.state, () => {
        handleCrossSectionConnect(app, viewer, crossSection);
        handleMaterialUpdate(app, viewer, crossSection);
    });

    subscribeKey(viewer.lod.state, "tables", () => {
        handleMaterialUpdate(app, viewer, crossSection);
    });

    let previousSelection = null as string | null;
    subscribeKey(app.state, "selectionId", (selectionId) => {
        const ids = [previousSelection, selectionId];
        previousSelection = selectionId;
        viewer.annotations.update(ids.filter((id): id is string => id !== null));
    });

    subscribe(app.state, () => {
        viewer.lod.eval();
        handleCrossSectionConnect(app, viewer, crossSection);
        handleMaterialUpdate(app, viewer, crossSection);
    });

    subscribe(app.state.rasters, () => {
        viewer.terrain.update();
    });

    subscribe(app.state.collections, () => {
        viewer.annotations.eval();
    });

    subscribe(prefs.state, () => {
        viewer.lod.eval();
        handleMaterialUpdate(app, viewer, crossSection);
    });

    // Keep physical camera distances constant when the scene unit changes.
    subscribeKey(app.state, "sceneUnit", () => {
        viewer.terrain.applyHeightScale();
        viewer.updateCameraNearFar();
        viewer.update();
    });
}

function handleCrossSectionSync(crossSection: ReturnType<typeof createCrossSection>) {
    subscribe(crossSection.state, () => {
        crossSection.lod.eval();
    });
}

const createViewer = (app: App, auth: Auth, prefs: Prefs, lumidb: LumiDB, journal: Journal, project: Project) => {
    const viewer = new Viewer(app);
    const terrain = new Terrain(viewer, app, project, auth, "auto");
    const lod = new LOD(viewer, app, prefs, lumidb, project);
    const annotations = new Annotations(viewer, prefs, journal, app, lod, project, terrain);
    const controls = new EarthControls(app, viewer, lod, terrain, project, annotations);
    const inputManager = new InputManager(annotations, controls);
    return Object.assign(viewer, { lod, annotations, controls, inputManager, terrain });
};

const createCrossSection = (
    viewer: ReturnType<typeof createViewer>,
    app: App,
    prefs: Prefs,
    lumidb: LumiDB,
    journal: Journal,
    project: Project,
) => {
    const crossSection = new CrossSection(viewer, app, prefs);
    const lod = new LOD(crossSection, app, prefs, lumidb, project);

    const annotations = new Annotations(crossSection, prefs, journal, app, lod, project);
    const inputManager = new InputManager(annotations, crossSection);
    return Object.assign(crossSection, { lod, annotations, inputManager });
};

const createServices = () => {
    const lumidb = new LumiDB({ baseUrl: "" });
    const auth = new Auth(lumidb);
    const journal = new Journal();
    const hud = new HUD();
    const app = new App(lumidb, auth);
    const prefs = new Prefs();
    const project = new Project(app, auth, hud);
    const org = new Org(auth, hud);
    const viewer = createViewer(app, auth, prefs, lumidb, journal, project);
    const history = new History(viewer.annotations, journal);
    const crossSection = createCrossSection(viewer, app, prefs, lumidb, journal, project);
    const scene = new Scene(app, project, viewer, prefs, hud, history, lumidb, crossSection);
    const thumbnail = new Thumbnail(lumidb, auth, project);

    handleViewerSync(app, prefs, viewer, crossSection);
    handleCrossSectionSync(crossSection);

    return { auth, lumidb, journal, app, prefs, project, org, viewer, history, hud, crossSection, scene, thumbnail };
};

let services: ReturnType<typeof createServices>;
if (globalThis.__services__) {
    services = globalThis.__services__;
} else {
    services = createServices();
    globalThis.__services__ = services;
}

const { auth, lumidb, journal, app, prefs, project, org, viewer, history, hud, crossSection, scene, thumbnail } =
    services;

function selectAppItem(id: string | null) {
    viewer.annotations.cancelDraft();
    app.state.selectionId = id;
}

export {
    app,
    auth,
    crossSection,
    history,
    hud,
    journal,
    lumidb,
    org,
    prefs,
    project,
    scene,
    selectAppItem,
    thumbnail,
    viewer,
};
